import { ArrowPathIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  OfferPublished,
  ReinsurersParticipation,
} from "graphql/__generated__/globalTypes";
import { FC, useState } from "react";

import { clsx } from "@mantine/core";
import { formatRelativeTime } from "utils";
import ActionButtons from "./ActionButtons";
import Countdown from "./Countdown";

interface OfferCardProps {
  offer: OfferPublished;
  hasParticipated?: boolean;
  currentParticipation?: ReinsurersParticipation;
  refetchOffers?: () => void;
  selected?: boolean;
  onSelect?: (id: string) => void;
}

const OfferCard: FC<OfferCardProps> = ({
  offer,
  hasParticipated,
  currentParticipation,
  refetchOffers,
  selected = false,
}) => {
  const isOneDay = (date: Date): boolean => {
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    return diffInHours >= 24;
  };

  const [isExpired, setIsExpired] = useState(
    offer.status === "EXPIRED" ||
    (offer.time_of_expiry && new Date(offer.time_of_expiry) < new Date()) ||
    (offer.created_at && isOneDay(new Date(offer.created_at)))
  );

  const isOfferClosed = offer.offer.offer_status === "CLOSED";
  const publishedOfferStatus = offer.reinsurer_participation
    ? "PENDING"
    : "OPEN";
  const isUpcomingRenewal = offer.is_upcoming_renewal;
  const isParticipationAccepted =
    offer.reinsurer_participation?.participation_status === "ACCEPTED";
  const isPlaceItOffer = offer.published_by?.toLowerCase() === "placeit";

  const handleExpired = () => {
    setIsExpired(true);
  };

  const cardBaseClasses =
    "relative bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 ease-in-out w-full min-w-[320px] border";
  const cardHoverClasses = "hover:shadow-xl hover:border-green-300";
  const cardSelectedClasses =
    "ring-2 ring-offset-1 ring-green-500 border-green-500";
  const cardUnselectedClasses = "border-gray-200";

  return (
    <div
      className={clsx(
        cardBaseClasses,
        selected ? cardSelectedClasses : cardUnselectedClasses,
        "cursor-pointer",
        !selected && cardHoverClasses,
        isParticipationAccepted && "border-l-4 border-l-green-500"
      )}
    >
      <div className="absolute top-3 right-3 z-10 flex flex-col items-end space-y-2">
        {isUpcomingRenewal && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-purple-100 text-purple-800">
            <ArrowPathIcon className="w-3 h-3 mr-1" />
            Renewal Offer
          </span>
        )}
        {isParticipationAccepted && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Accepted
          </span>
        )}
        <span
          className={clsx(
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm",
            publishedOfferStatus === "PENDING"
              ? "bg-yellow-100 text-red-800"
              : "bg-green-100 text-green-800"
          )}
        >
          {publishedOfferStatus}
        </span>
        {!isExpired && !isOfferClosed && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-blue-100 text-blue-800">
            Live
          </span>
        )}
        {isOfferClosed && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-gray-100 text-gray-800">
            Closed
          </span>
        )}
      </div>

      <div className="p-5">
        <div className="mb-4 pr-16">
          <h3
            className="text-lg font-semibold text-gray-800 truncate"
            title={
              isPlaceItOffer
                ? offer.offer.class_of_business?.business_name || "N/A"
                : offer.offer.classofbusiness?.business_name || "N/A"
            }
          >
            {isPlaceItOffer
              ? offer.offer.class_of_business?.business_name || "N/A"
              : offer.offer.classofbusiness?.business_name || "N/A"}{" "}
          </h3>
          <p
            className="text-md text-gray-600 font-bold truncate"
            title={`Insured: ${offer.offer.offer_detail?.insured_by || "N/A"}`}
          >
            Insured: {offer.offer.offer_detail?.insured_by || "N/A"}
          </p>
          <p className="text-sm text-gray-600 truncate">
            Fac Offer: {offer.offer.facultative_offer || "N/A"}%
          </p>
          {/* <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Published By:</span>
            <PublisherPill publishedBy={offer.published_by} />
          </div> */}
          {offer?.participated_by && (
            <p className="text-sm text-gray-600 truncate">
              Published By: {offer.offer.insurer?.insurer_company_name || "N/A"}
            </p>
          )}
          {offer?.participated_by && (
            <p className="text-sm text-gray-600 truncate">
              Participated By: {offer.participated_by}
            </p>
          )}
          {offer?.reinsurer_participation &&
            offer.reinsurer_participation.offer_participant_percentage !==
            0 && (
              <p
                className={clsx(
                  "text-sm truncate",
                  isParticipationAccepted
                    ? "text-green-600 font-semibold"
                    : "text-red-600"
                )}
              >
                Your {isParticipationAccepted ? "Accepted" : "Proposed"}{" "}
                Interest:{" "}
                {offer.reinsurer_participation.offer_participant_percentage}%
              </p>
            )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Premium
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer.offer_detail?.currency}{" "}
              {offer.offer.premium
                ? Number(offer.offer.premium).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                : "N/A"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sum Insured
            </p>
            <p
              className="font-medium text-gray-700 truncate"
              title={
                offer.offer.sum_insured
                  ? Number(offer.offer.sum_insured).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                  : "N/A"
              }
            >
              {offer.offer.offer_detail?.currency}{" "}
              {offer.offer.sum_insured
                ? Number(offer.offer.sum_insured).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                : "N/A"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Period
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer.offer_detail?.period_of_insurance_from
                ? new Date(
                  offer.offer.offer_detail.period_of_insurance_from
                ).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                : "N/A"}
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer.offer_detail?.period_of_insurance_to
                ? new Date(
                  offer.offer.offer_detail.period_of_insurance_to
                ).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                : "N/A"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Policy Number
            </p>
            <p
              className="font-medium text-gray-700 truncate"
              title={offer.offer.offer_detail?.policy_number || "N/A"}
            >
              {offer.offer.offer_detail?.policy_number || "N/A"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Published
            </p>
            <p className="font-medium text-gray-700">
              {offer.created_at
                ? formatRelativeTime(new Date(offer.created_at))
                : "N/A"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reinsured
            </p>
            <p className="font-medium text-gray-700">
              {offer.offer.insurer?.insurer_company_name || "N/A"}
            </p>
          </div>
        </div>

        {offer.time_of_expiry &&
          !isExpired &&
          !isOfferClosed &&
          !hasParticipated && (
            <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
              <Countdown
                expiryTime={offer.time_of_expiry}
                onExpired={handleExpired}
              />
            </div>
          )}

        {isParticipationAccepted && (
          <div className="mb-4 bg-green-50 p-3 rounded-md border border-green-200 flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">
              Your participation has been accepted
            </span>
          </div>
        )}

        <div className="border-t border-gray-200 my-4"></div>

        <div className="action-buttons-container">
          <ActionButtons
            offer={offer}
            hasParticipated={hasParticipated}
            currentParticipation={currentParticipation || null}
            unreadMessages={offer.unread_messages || null}
            refetchOffers={refetchOffers}
          />
        </div>
      </div>
    </div>
  );
};

export default OfferCard;
